import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import OneSNavPanel from "@/components/feature/OneSNavPanel";
import { useDiscussions } from "@/hooks/useDiscussions";
import { client360Data, client360Tabs } from "@/mocks/client360";
import AiInsights from "@/pages/client360/components/AiInsights";

function formatMoney(value: number): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(value);
}

const statusColor = (status: string) => {
  switch (status) {
    case "Новый": return "bg-[#E8F4FD] text-[#1E6FAD]";
    case "Активный": return "bg-[#D4EDDA] text-[#2D6A4F]";
    case "Уснувший": return "bg-[#FDF2B5] text-[#8B7E3A]";
    case "Потерянный": return "bg-[#FFEDD5] text-[#DC2626]";
    case "VIP": return "bg-[#FEF3C7] text-[#8B6914]";
    case "Должник": return "bg-[#FFEDD5] text-[#DC2626]";
    default: return "bg-[#D4EDDA] text-[#2D6A4F]";
  }
};

const statusIcon = (status: string) => {
  switch (status) {
    case "Новый": return "ri-sparkling-line";
    case "Активный": return "ri-checkbox-circle-line";
    case "Уснувший": return "ri-moon-line";
    case "Потерянный": return "ri-close-circle-line";
    case "VIP": return "ri-vip-crown-line";
    case "Должник": return "ri-error-warning-line";
    default: return "ri-checkbox-circle-line";
  }
};

function InfoBlock({ label, value, valueClass = "text-[#2D2D2D]" }: { label: string; value: string | number; valueClass?: string }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-[10px] text-[#666666] shrink-0">{label}</span>
      <span className={`text-[10px] font-medium text-right ${valueClass}`}>{value}</span>
    </div>
  );
}

export default function Client360Page() {
  const navigate = useNavigate();
  const { toggle: toggleDiscussions } = useDiscussions();
  const [activeTab, setActiveTab] = useState("Обзор");
  const data = client360Data;
  const [clientStatus, setClientStatus] = useState(data.partnerStatus);
  const [statusOpen, setStatusOpen] = useState(false);
  const statusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) {
        setStatusOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="h-screen flex flex-col bg-[#FFF9E6] overflow-hidden"
      style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
    >
      {/* 1C Title Bar */}
      <div className="h-[28px] flex items-center justify-between bg-[#FBED9E] px-3 flex-shrink-0 select-none border-b border-[#D5CA8A]">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <i className="ri-menu-line text-[#666666] text-[13px]"></i>
            <span className="text-[11px] text-[#2D2D2D] font-medium">Методическая база / Управление торговлей, редакция 11 (1С:Предприятие)</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-[#666666]">Елена Голубева</span>
          <div className="w-px h-3 bg-[#D5CA8A]" />
          <i className="ri-settings-3-line text-[#666666] text-[13px] cursor-pointer hover:text-[#2D2D2D]"></i>
          <i className="ri-question-line text-[#666666] text-[13px] cursor-pointer hover:text-[#2D2D2D]"></i>
          <i className="ri-close-line text-[#666666] text-[13px] cursor-pointer hover:text-[#2D2D2D]"></i>
        </div>
      </div>

      {/* 1C Command Bar */}
      <div className="h-[32px] flex items-center gap-1 px-2 bg-[#FFF9E6] border-b border-[#D5CA8A] flex-shrink-0 select-none">
        <div className="flex items-center gap-1">
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors">
            <i className="ri-file-add-line text-[11px]"></i>
            <span>Создать</span>
          </button>
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors">
            <i className="ri-file-copy-line text-[11px]"></i>
            <span>Скопировать</span>
          </button>
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors">
            <i className="ri-delete-bin-line text-[11px]"></i>
            <span>Удалить</span>
          </button>
        </div>
        <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
        <div className="flex items-center gap-1">
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors">
            <i className="ri-filter-line text-[11px]"></i>
            <span>Фильтры</span>
          </button>
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors">
            <i className="ri-group-line text-[11px]"></i>
            <span>Группировки</span>
          </button>
        </div>
        <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
        <div className="flex items-center gap-1">
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors">
            <i className="ri-file-list-3-line text-[11px]"></i>
            <span>Отчёт</span>
          </button>
          <button className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors">
            <i className="ri-printer-line text-[11px]"></i>
            <span>Печать</span>
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors"
          >
            <i className="ri-arrow-left-line text-[11px]"></i>
            <span>Заказы клиентов</span>
          </button>
          <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
          <button
            onClick={() => navigate("/rop")}
            className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors"
          >
            <i className="ri-vip-crown-line text-[11px]"></i>
            <span>РОП</span>
          </button>
          <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
          <div className="flex items-center gap-1 bg-[#FDF2B5] rounded p-[2px]">
            <span className="px-2 py-[2px] bg-[#E8DB6C] text-[#2D2D2D] rounded text-[11px] font-medium flex items-center gap-1">
              <i className="ri-user-search-line text-[10px]"></i>
              Клиент 360
            </span>
          </div>
          <button
            onClick={() => navigate("/manager-workspace")}
            className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors"
          >
            <i className="ri-user-settings-line text-[10px]"></i>
            <span>Рабочее место</span>
          </button>
          <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
          <button
            onClick={toggleDiscussions}
            className="flex items-center gap-1 px-2 py-[2px] text-[#2D2D2D] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors whitespace-nowrap"
          >
            <i className="ri-chat-3-line text-[11px]"></i>
            <span>Обсуждения</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="h-[32px] flex items-center bg-[#FFF9E6] border-b border-[#D5CA8A] px-1 gap-0 select-none">
        <button className="flex items-center gap-1 px-2 py-1 text-[#666666] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors">
          <i className="ri-arrow-left-s-line text-[11px]"></i>
        </button>
        <button className="flex items-center gap-1 px-2 py-1 text-[#666666] hover:bg-[#FDF2B5] rounded cursor-pointer text-[11px] transition-colors">
          <i className="ri-arrow-right-s-line text-[11px]"></i>
        </button>
        <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-1 px-3 py-1 text-[11px] rounded-t cursor-pointer transition-colors border border-b-0 bg-[#FFF9E6] text-[#666666] border-[#FFF9E6] hover:bg-[#FDF2B5]"
        >
          <i className="ri-file-list-2-line text-[10px]"></i>
          Заказы клиентов
        </button>
        <button className="flex items-center gap-1 px-3 py-1 text-[11px] rounded-t cursor-pointer transition-colors border border-b-0 bg-white text-[#2D2D2D] border-[#D5CA8A] font-medium">
          <i className="ri-user-search-line text-[10px]"></i>
          CRM 360 клиента
          <i className="ri-close-line text-[10px] hover:text-[#666666] ml-1"></i>
        </button>
        <div className="ml-auto flex items-center gap-1 pr-2 text-[10px]">
          <span className="text-[#666666]">Методическая база</span>
          <span className="text-[#666666]">/</span>
          <span className="text-[#666666]">CRM</span>
          <span className="text-[#666666]">/</span>
          <span className="text-[#2D2D2D] font-medium">CRM 360 партнера</span>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex overflow-hidden">
        {/* 1C Navigation Panel */}
        <OneSNavPanel />

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Header Card */}
          <div className="bg-white border-b border-[#D5CA8A] p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <h1 className="text-[18px] font-bold text-[#2D2D2D]">CRM 360° клиента</h1>
                <i className="ri-star-line text-[#D5CA8A] text-[14px] cursor-pointer"></i>
              </div>
              <div className="flex items-center gap-2">
                <button className="flex items-center gap-1 px-3 py-1.5 rounded border border-[#D5CA8A] text-[11px] text-[#2D2D2D] hover:bg-[#FDF2B5] transition-colors cursor-pointer">
                  <i className="ri-phone-line text-[11px]"></i>
                  Позвонить
                </button>
                <button className="flex items-center gap-1 px-3 py-1.5 rounded border border-[#D5CA8A] text-[11px] text-[#2D2D2D] hover:bg-[#FDF2B5] transition-colors cursor-pointer">
                  <i className="ri-mail-send-line text-[11px]"></i>
                  Написать
                </button>
                <button className="flex items-center gap-1 px-3 py-1.5 rounded border border-[#D5CA8A] text-[11px] text-[#2D2D2D] hover:bg-[#FDF2B5] transition-colors cursor-pointer">
                  Ещё
                  <i className="ri-arrow-down-s-line text-[11px]"></i>
                </button>
                <button className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#FDF2B5] cursor-pointer">
                  <i className="ri-close-line text-[#666666] text-[12px]"></i>
                </button>
              </div>
            </div>

            <div className="flex items-start gap-6">
              {/* Left: Client info — wider, has more fields */}
              <div className="flex-[3] min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[14px] font-semibold text-[#2D2D2D]">{data.name}</span>
                  <div className="relative" ref={statusRef}>
                    <button
                      onClick={() => setStatusOpen(!statusOpen)}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium cursor-pointer transition-colors ${statusColor(clientStatus)}`}
                    >
                      <i className={`${statusIcon(clientStatus)} text-[10px]`}></i>
                      {clientStatus} клиент
                      <i className="ri-arrow-down-s-line text-[9px]"></i>
                    </button>
                    {statusOpen && (
                      <div className="absolute top-full left-0 mt-1 bg-white rounded shadow-lg border border-[#D5CA8A] z-50 min-w-[150px] py-1">
                        {data.clientStatusOptions.map((status) => (
                          <button
                            key={status}
                            onClick={() => { setClientStatus(status); setStatusOpen(false); }}
                            className={`flex items-center gap-2 w-full px-3 py-1.5 text-[10px] text-left hover:bg-[#FDF2B5] transition-colors cursor-pointer ${clientStatus === status ? "bg-[#FDF2B5]/50 font-medium" : "text-[#2D2D2D]"}`}
                          >
                            <i className={`${statusIcon(status)} text-[10px]`}></i>
                            {status}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <i className="ri-star-line text-[#D5CA8A] text-[12px] cursor-pointer"></i>
                </div>
                <div className="flex items-center gap-1 mb-3">
                  {data.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 rounded border border-[#D5CA8A] text-[10px] text-[#666666]">{tag}</span>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[10px]">
                  <div className="flex items-center gap-2">
                    <span className="text-[#666666] w-[60px] shrink-0">ИНН</span>
                    <span className="text-[#2D2D2D] font-medium">{data.inn}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#666666] w-[60px] shrink-0">КПП</span>
                    <span className="text-[#2D2D2D] font-medium">{data.kpp}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#666666] w-[60px] shrink-0">БИН</span>
                    <span className="text-[#2D2D2D] font-medium">{data.bin}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-[#666666] w-[60px] shrink-0">Юр. адрес</span>
                    <span className="text-[#2D2D2D] font-medium">{data.legalAddress}</span>
                  </div>
                </div>
              </div>

              {/* Manager — compact, avatar + name only */}
              <div className="flex-[1.5] min-w-0">
                <p className="text-[10px] text-[#666666] mb-2">Основной менеджер</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-[#D5CA8A] flex items-center justify-center shrink-0">
                    <img src={data.managerAvatar} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-[#2D2D2D]">{data.managerName}</p>
                    <p className="text-[9px] text-[#666666] cursor-pointer hover:text-[#2D2D2D]">Изменить</p>
                  </div>
                </div>
              </div>

              {/* Sales info — medium width, monetary values */}
              <div className="flex-[2] min-w-0">
                <div className="space-y-2">
                  <div>
                    <p className="text-[10px] text-[#666666] mb-0.5">Сумма продаж (12 мес.)</p>
                    <p className="text-[14px] font-bold text-[#2D2D2D]">{formatMoney(data.sales12m)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#666666] mb-0.5">Сумма долга</p>
                    <p className="text-[14px] font-bold text-[#DC2626]">{formatMoney(data.debt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs row */}
          <div className="flex items-center bg-white border-b border-[#D5CA8A] px-4 gap-0 select-none overflow-x-auto">
            {client360Tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-2 text-[11px] cursor-pointer transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === tab
                    ? "border-[#2D6A4F] text-[#2D6A4F] font-medium"
                    : "border-transparent text-[#666666] hover:text-[#2D2D2D]"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {activeTab === "Обзор" && (
              <div className="flex gap-4">
                {/* Main content */}
                <div className="flex-1 min-w-0">
                  {/* Top 4 cards */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
                    {/* Key info */}
                    <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                      <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
                        <span className="text-[11px] font-semibold text-[#2D2D2D]">Ключевая информация</span>
                      </div>
                      <div className="p-3 space-y-2">
                        <InfoBlock label="Сегмент" value={data.segment} />
                        <InfoBlock label="Регион" value={data.region} />
                        <InfoBlock label="Сфера деятельности" value="Производство одежды" />
                        <InfoBlock label="Источник клиента" value={data.source} />
                        <InfoBlock label="Комментарий" value={data.comment} />
                        <div className="pt-1">
                          <span className="text-[10px] text-[#666666]">Теги</span>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            {data.tagsExtra.map((tag) => (
                              <span key={tag} className="px-2 py-0.5 rounded bg-[#FDF2B5] text-[#2D2D2D] text-[10px] border border-[#D5CA8A]">{tag}</span>
                            ))}
                            <button className="w-5 h-5 flex items-center justify-center rounded border border-[#D5CA8A] text-[#666666] text-[10px] hover:bg-[#FDF2B5] cursor-pointer">+</button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Financial info */}
                    <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                      <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
                        <span className="text-[11px] font-semibold text-[#2D2D2D]">Финансовая информация</span>
                      </div>
                      <div className="p-3 space-y-2">
                        <InfoBlock label="Текущий долг" value={formatMoney(data.currentDebt)} valueClass="text-[#DC2626] font-bold" />
                        <InfoBlock label="Просроченная задолженность" value={formatMoney(data.overdueDebt)} valueClass="text-[#DC2626]" />
                        <InfoBlock label="Лимит кредита" value={formatMoney(data.creditLimit)} />
                        <InfoBlock label="Доступный лимит" value={formatMoney(data.availableLimit)} valueClass="text-[#2D6A4F] font-bold" />
                        <div className="pt-1">
                          <button className="text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">Подробнее о долгах</button>
                        </div>
                      </div>
                    </div>

                    {/* Recent interactions */}
                    <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                      <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
                        <span className="text-[11px] font-semibold text-[#2D2D2D]">Последние взаимодействия</span>
                      </div>
                      <div className="p-3 space-y-2">
                        {data.interactions.map((interaction, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 bg-[#FDF2B5]">
                              <i className={`${interaction.icon} text-[12px] text-[#2D6A4F]`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] text-[#2D2D2D] font-medium truncate">{interaction.title}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[9px] text-[#666666]">{interaction.date} {interaction.time}</span>
                                <span className="text-[9px] text-[#666666]">{interaction.author}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="pt-1">
                          <button className="text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">Все истории</button>
                        </div>
                      </div>
                    </div>

                    {/* Next action */}
                    <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                      <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
                        <span className="text-[11px] font-semibold text-[#2D2D2D]">Следующее действие</span>
                      </div>
                      <div className="p-3 space-y-2">
                        <p className="text-[10px] text-[#2D2D2D] font-medium leading-relaxed">{data.nextAction.title}</p>
                        <div className="space-y-1">
                          <InfoBlock label="Дата и время" value={`${data.nextAction.date} ${data.nextAction.time}`} />
                          <InfoBlock label="Тип" value={data.nextAction.type} />
                          <InfoBlock label="Ответственный" value={data.nextAction.assignee} />
                        </div>
                        <button className="w-full mt-2 py-1.5 rounded bg-[#2D6A4F] text-white text-[11px] font-medium hover:bg-[#1E4D3A] transition-colors cursor-pointer">
                          Выполнить действие
                        </button>
                        <div className="text-center">
                          <button className="text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">Все задачи (3)</button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Middle row: Contracts, Sales, Top Products */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
                    {/* Contracts */}
                    <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                      <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-[#2D2D2D]">Клиент и дочерние компании</span>
                        <button className="text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">Карточка клиента</button>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[11px] font-semibold text-[#2D2D2D]">{data.name}</span>
                          <span className="px-1.5 py-0.5 rounded bg-[#D4EDDA] text-[#2D6A4F] text-[9px]">Основной контрагент</span>
                        </div>
                        <div className="space-y-1 mb-2">
                          <div className="flex items-center text-[10px] text-[#666666] px-1 py-0.5 bg-[#FDF2B5] rounded">
                            <span className="flex-1">Дочерняя компания</span>
                            <span className="w-[65px]">Тип цен</span>
                            <span className="w-[120px] text-right">Долг, ₽</span>
                          </div>
                          {data.contracts.map((contract, i) => (
                            <div key={i} className="flex items-center text-[10px] px-1 py-1 border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/30 rounded transition-colors">
                              <span className="flex-1 text-[#2D2D2D] truncate">{contract.isPrimary ? "★ " : ""}{contract.name}</span>
                              <span className="w-[65px] text-[#666666]">{contract.type}</span>
                              <span className={`w-[120px] text-right ${contract.debt > 0 ? "text-[#DC2626]" : "text-[#2D2D2D]"}`}>{contract.debt > 0 ? contract.debt.toLocaleString("ru-RU") : "—"}</span>
                            </div>
                          ))}
                        </div>
                        <button className="text-[10px] text-[#2D6A4F] hover:underline cursor-pointer mb-2">+ Добавить дочернюю компанию</button>
                        <div className="p-2 bg-[#FDF2B5]/40 rounded text-[9px] text-[#666666] leading-relaxed">
                          Основная компания используется для выписки счетов и документов по умолчанию.
                          Неактивные компании без долга могут быть скрыты настройкой.
                          Компания с долгом всегда отображается.
                        </div>
                      </div>
                    </div>

                    {/* Sales & Orders */}
                    <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                      <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
                        <span className="text-[11px] font-semibold text-[#2D2D2D]">Продажи и заказы</span>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center text-[10px] text-[#666666] px-1 py-0.5 bg-[#FDF2B5] rounded mb-2">
                          <span className="flex-1">Период</span>
                          <span className="w-[80px] text-right">Сумма продаж</span>
                          <span className="w-[60px] text-right">Кол-во заказов</span>
                        </div>
                        {data.salesDynamics.map((period, i) => (
                          <div key={i} className="flex items-center text-[10px] px-1 py-1 border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/30 rounded transition-colors">
                            <span className="flex-1 text-[#2D2D2D]">{period.period}</span>
                            <span className="w-[80px] text-right text-[#2D2D2D] font-medium">{period.amount.toLocaleString("ru-RU")}</span>
                            <span className="w-[60px] text-right text-[#666666]">{period.orders}</span>
                          </div>
                        ))}
                        <div className="mt-1 text-right">
                          <button className="text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">Все заказы</button>
                        </div>
                        {/* Mini chart */}
                        <div className="mt-3">
                          <p className="text-[9px] text-[#666666] mb-1">Динамика продаж за 12 месяцев</p>
                          <div className="flex items-end gap-1 h-[60px] px-1">
                            {data.salesChart.map((val, i) => {
                              const max = Math.max(...data.salesChart);
                              const height = max > 0 ? (val / max) * 100 : 0;
                              return (
                                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                                  <div className="w-full bg-[#2D6A4F] rounded-t" style={{ height: `${height}%` }}></div>
                                  <span className="text-[7px] text-[#666666]">{["май","июн","июл","авг","сен","окт","ноя","дек","янв","фев","мар","апр"][i]}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Top Products */}
                    <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                      <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
                        <span className="text-[11px] font-semibold text-[#2D2D2D]">Покупаемые товары (топ-5)</span>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center text-[10px] text-[#666666] px-1 py-0.5 bg-[#FDF2B5] rounded mb-2">
                          <span className="flex-1">Товарная группа</span>
                          <span className="w-[80px] text-right">Сумма</span>
                        </div>
                        {data.topProducts.map((product, i) => (
                          <div key={i} className="flex items-center text-[10px] px-1 py-1 border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/30 rounded transition-colors">
                            <span className="flex-1 text-[#2D2D2D]">{product.group}</span>
                            <span className="w-[80px] text-right text-[#2D2D2D] font-medium">{product.amount.toLocaleString("ru-RU")}</span>
                          </div>
                        ))}
                        <div className="mt-1 text-right">
                          <button className="text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">Все товары</button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AI Insights & Cross-sell row */}
                  <AiInsights data={data} />

                </div>
              </div>
            )}

            {/* Other tabs placeholder */}
            {activeTab === "Сделки и заказы" && (
              <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-[#2D2D2D]">Сделки и заказы ({data.deals.length})</span>
                  <button className="flex items-center gap-1 text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">
                    <i className="ri-add-line text-[10px]"></i>
                    Создать
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="bg-[#FDF2B5] text-[#666666]">
                        <th className="text-left px-3 py-2 font-medium">Номер</th>
                        <th className="text-left px-3 py-2 font-medium">Тип</th>
                        <th className="text-left px-3 py-2 font-medium">Содержание</th>
                        <th className="text-left px-3 py-2 font-medium">Дата</th>
                        <th className="text-right px-3 py-2 font-medium">Сумма, ₽</th>
                        <th className="text-left px-3 py-2 font-medium">Статус</th>
                        <th className="text-left px-3 py-2 font-medium">Этап</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.deals.map((deal) => (
                        <tr key={deal.id} className="border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/20 transition-colors cursor-pointer">
                          <td className="px-3 py-2.5 text-[#2D6A4F] font-medium whitespace-nowrap">{deal.id}</td>
                          <td className="px-3 py-2.5">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] whitespace-nowrap ${deal.type === "Сделка" ? "bg-[#FEF3C7] text-[#2D2D2D]" : "bg-[#D4EDDA] text-[#2D6A4F]"}`}>
                              {deal.type}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <div>
                              <p className="text-[#2D2D2D] font-medium">{deal.title}</p>
                              <p className="text-[9px] text-[#666666] mt-0.5">{deal.products}</p>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-[#666666] whitespace-nowrap">{deal.date}</td>
                          <td className="px-3 py-2.5 text-right text-[#2D2D2D] font-medium whitespace-nowrap">{deal.amount.toLocaleString("ru-RU")}</td>
                          <td className="px-3 py-2.5">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] whitespace-nowrap ${
                              deal.status === "Отгружен" || deal.status === "Закрыта"
                                ? "bg-[#D4EDDA] text-[#2D6A4F]"
                                : deal.status === "В работе"
                                ? "bg-[#FEF3C7] text-[#2D2D2D]"
                                : "bg-[#FDF2B5] text-[#2D2D2D]"
                            }`}>{deal.status}</span>
                          </td>
                          <td className="px-3 py-2.5 text-[#666666] whitespace-nowrap">{deal.stage}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Other tabs placeholder */}
            {activeTab === "Контакты" && (
              <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-[#2D2D2D]">Контакты ({data.contacts.length})</span>
                  <button className="flex items-center gap-1 text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">
                    <i className="ri-add-line text-[10px]"></i>
                    Добавить контакт
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="bg-[#FDF2B5] text-[#666666]">
                        <th className="text-left px-3 py-2 font-medium">ФИО</th>
                        <th className="text-left px-3 py-2 font-medium">Должность</th>
                        <th className="text-left px-3 py-2 font-medium">Телефон</th>
                        <th className="text-left px-3 py-2 font-medium">Email</th>
                        <th className="text-center px-3 py-2 font-medium">ЛПР</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.contacts.map((contact, i) => (
                        <tr key={i} className="border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/20 transition-colors cursor-pointer">
                          <td className="px-3 py-2.5 text-[#2D2D2D] font-medium">{contact.name}</td>
                          <td className="px-3 py-2.5 text-[#666666]">{contact.role}</td>
                          <td className="px-3 py-2.5 text-[#2D2D2D]">
                            <div className="flex items-center gap-1">
                              <span>{contact.phone}</span>
                              <i className="ri-phone-line text-[#2D6A4F] text-[10px] cursor-pointer"></i>
                              <i className="ri-whatsapp-line text-[#2D6A4F] text-[10px] cursor-pointer"></i>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-[#2D6A4F]">
                            <span className="cursor-pointer hover:underline">{contact.email}</span>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {contact.isDecisionMaker && (
                              <span className="px-1.5 py-0.5 rounded bg-[#D4EDDA] text-[#2D6A4F] text-[9px] font-medium">ЛПР</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "Клиент и дочерние компании" && (
              <div className="space-y-3">
                <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                  <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-[#2D2D2D]">Структура компаний ({data.contracts.length})</span>
                    <button className="flex items-center gap-1 text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">
                      <i className="ri-add-line text-[10px]"></i>
                      Добавить компанию
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="bg-[#FDF2B5] text-[#666666]">
                          <th className="text-left px-3 py-2 font-medium">Наименование</th>
                          <th className="text-left px-3 py-2 font-medium">Тип цен</th>
                          <th className="text-left px-3 py-2 font-medium">Статус</th>
                          <th className="text-right px-3 py-2 font-medium">Долг, ₽</th>
                          <th className="text-left px-3 py-2 font-medium">Роль</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.contracts.map((contract, i) => (
                          <tr key={i} className="border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/20 transition-colors cursor-pointer">
                            <td className="px-3 py-2.5 text-[#2D2D2D] font-medium">{contract.isPrimary ? "★ " : ""}{contract.name}</td>
                            <td className="px-3 py-2.5 text-[#666666]">{contract.type}</td>
                            <td className="px-3 py-2.5">
                              <span className={`px-1.5 py-0.5 rounded text-[9px] ${contract.status === "Активный" ? "bg-[#D4EDDA] text-[#2D6A4F]" : "bg-[#FDF2B5] text-[#666666]"}`}>{contract.status}</span>
                            </td>
                            <td className={`px-3 py-2.5 text-right ${contract.debt > 0 ? "text-[#DC2626] font-medium" : "text-[#666666]"}`}>
                              {contract.debt > 0 ? contract.debt.toLocaleString("ru-RU") : "—"}
                            </td>
                            <td className="px-3 py-2.5 text-[#666666]">
                              {contract.isPrimary ? "Основная" : "Дочерняя"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="p-2 bg-[#FDF2B5]/40 text-[9px] text-[#666666] leading-relaxed">
                    Основная компания используется для выписки счетов и документов по умолчанию. Неактивные компании без долга могут быть скрыты настройкой. Компания с долгом всегда отображается.
                  </div>
                </div>
              </div>
            )}

            {activeTab === "История взаимодействий" && (
              <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-[#2D2D2D]">История взаимодействий ({data.interactions.length})</span>
                  <button className="flex items-center gap-1 text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">
                    <i className="ri-add-line text-[10px]"></i>
                    Добавить
                  </button>
                </div>
                <div className="p-3">
                  <div className="relative pl-6 space-y-4 border-l-2 border-[#D5CA8A] ml-3">
                    {data.interactions.map((interaction, i) => (
                      <div key={i} className="relative">
                        <div className="absolute -left-[33px] top-1 w-8 h-8 rounded-full bg-[#FDF2B5] border-2 border-[#D5CA8A] flex items-center justify-center">
                          <i className={`${interaction.icon} text-[14px] text-[#2D6A4F]`}></i>
                        </div>
                        <div className="bg-[#FDF2B5]/30 rounded p-3 ml-2">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-[11px] text-[#2D2D2D] font-medium">{interaction.title}</p>
                            <span className="text-[9px] text-[#666666] whitespace-nowrap ml-2">{interaction.date} {interaction.time}</span>
                          </div>
                          <p className="text-[9px] text-[#666666]">{interaction.author}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-center">
                    <button className="text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">Загрузить ещё</button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "Оплаты и долги" && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                    <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
                      <span className="text-[11px] font-semibold text-[#2D2D2D]">Структура задолженности</span>
                    </div>
                    <div className="p-3 space-y-1">
                      <div className="flex items-center text-[10px] text-[#666666] px-1 py-0.5 bg-[#FDF2B5] rounded">
                        <span className="flex-1">Статья</span>
                        <span className="w-[100px] text-right">Сумма, ₽</span>
                      </div>
                      {data.debtsDetail.map((debt, i) => (
                        <div key={i} className="flex items-center text-[10px] px-1 py-1 border-b border-[#D5CA8A]/30">
                          <span className="flex-1 text-[#2D2D2D]">{debt.label}</span>
                          <span className={`w-[100px] text-right font-medium ${debt.overdue ? "text-[#DC2626]" : "text-[#2D2D2D]"}`}>
                            {debt.amount.toLocaleString("ru-RU")}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center text-[10px] px-1 py-1 font-bold">
                        <span className="flex-1 text-[#2D2D2D]">ИТОГО</span>
                        <span className="w-[100px] text-right text-[#DC2626]">
                          {data.debtsDetail.reduce((s, d) => s + d.amount, 0).toLocaleString("ru-RU")}
                        </span>
                      </div>
                      <div className="mt-2 p-2 bg-[#FDF2B5]/40 rounded text-[9px] text-[#666666]">
                        Лимит кредита: {data.creditLimit.toLocaleString("ru-RU")} ₽ | Доступно: {data.availableLimit.toLocaleString("ru-RU")} ₽
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                    <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A]">
                      <span className="text-[11px] font-semibold text-[#2D2D2D]">Финансовые показатели</span>
                    </div>
                    <div className="p-3 space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-[#FDF2B5]/30 rounded p-2">
                          <p className="text-[9px] text-[#666666]">Продажи за 12 мес.</p>
                          <p className="text-[12px] font-bold text-[#2D2D2D]">{formatMoney(data.sales12m)}</p>
                        </div>
                        <div className="bg-[#FDF2B5]/30 rounded p-2">
                          <p className="text-[9px] text-[#666666]">Текущий долг</p>
                          <p className="text-[12px] font-bold text-[#DC2626]">{formatMoney(data.debt)}</p>
                        </div>
                        <div className="bg-[#FDF2B5]/30 rounded p-2">
                          <p className="text-[9px] text-[#666666]">Лимит кредита</p>
                          <p className="text-[12px] font-bold text-[#2D2D2D]">{formatMoney(data.creditLimit)}</p>
                        </div>
                        <div className="bg-[#FDF2B5]/30 rounded p-2">
                          <p className="text-[9px] text-[#666666]">Просрочено</p>
                          <p className="text-[12px] font-bold text-[#DC2626]">{formatMoney(data.overdueDebt)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                  <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-[#2D2D2D]">История платежей ({data.payments.length})</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="bg-[#FDF2B5] text-[#666666]">
                          <th className="text-left px-3 py-2 font-medium">Номер</th>
                          <th className="text-left px-3 py-2 font-medium">Дата</th>
                          <th className="text-left px-3 py-2 font-medium">Основание</th>
                          <th className="text-left px-3 py-2 font-medium">Тип</th>
                          <th className="text-right px-3 py-2 font-medium">Сумма, ₽</th>
                          <th className="text-left px-3 py-2 font-medium">Статус</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.payments.map((payment) => (
                          <tr key={payment.id} className="border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/20 transition-colors cursor-pointer">
                            <td className="px-3 py-2.5 text-[#2D6A4F] font-medium">{payment.id}</td>
                            <td className="px-3 py-2.5 text-[#666666]">{payment.date}</td>
                            <td className="px-3 py-2.5 text-[#2D2D2D]">{payment.docNumber}</td>
                            <td className="px-3 py-2.5 text-[#666666]">{payment.type}</td>
                            <td className="px-3 py-2.5 text-right text-[#2D2D2D] font-medium">{payment.amount.toLocaleString("ru-RU")}</td>
                            <td className="px-3 py-2.5">
                              <span className="px-1.5 py-0.5 rounded bg-[#D4EDDA] text-[#2D6A4F] text-[9px]">{payment.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "Товары и цены" && (
              <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-[#2D2D2D]">Товары и цены для клиента ({data.clientProducts.length})</span>
                  <button className="flex items-center gap-1 text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">
                    <i className="ri-add-line text-[10px]"></i>
                    Добавить товар
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="bg-[#FDF2B5] text-[#666666]">
                        <th className="text-left px-3 py-2 font-medium">Группа</th>
                        <th className="text-left px-3 py-2 font-medium">Товар</th>
                        <th className="text-left px-3 py-2 font-medium">Артикул</th>
                        <th className="text-right px-3 py-2 font-medium">Цена, ₽</th>
                        <th className="text-center px-3 py-2 font-medium">Ед.</th>
                        <th className="text-right px-3 py-2 font-medium">Мин. заказ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.clientProducts.map((product, i) => (
                        <tr key={i} className="border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/20 transition-colors cursor-pointer">
                          <td className="px-3 py-2.5">
                            <span className="px-1.5 py-0.5 rounded bg-[#FDF2B5] text-[#2D2D2D] text-[9px]">{product.group}</span>
                          </td>
                          <td className="px-3 py-2.5 text-[#2D2D2D] font-medium">{product.product}</td>
                          <td className="px-3 py-2.5 text-[#666666]">{product.article}</td>
                          <td className="px-3 py-2.5 text-right text-[#2D2D2D] font-medium">{product.price.toLocaleString("ru-RU")}</td>
                          <td className="px-3 py-2.5 text-center text-[#666666]">{product.unit}</td>
                          <td className="px-3 py-2.5 text-right text-[#666666]">{product.minOrder.toLocaleString("ru-RU")} {product.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "Документы" && (
              <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-[#2D2D2D]">Документы ({data.documents.length})</span>
                  <button className="flex items-center gap-1 text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">
                    <i className="ri-add-line text-[10px]"></i>
                    Создать документ
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="bg-[#FDF2B5] text-[#666666]">
                        <th className="text-left px-3 py-2 font-medium">Тип документа</th>
                        <th className="text-left px-3 py-2 font-medium">Номер</th>
                        <th className="text-left px-3 py-2 font-medium">Дата</th>
                        <th className="text-right px-3 py-2 font-medium">Сумма, ₽</th>
                        <th className="text-left px-3 py-2 font-medium">Статус</th>
                        <th className="text-left px-3 py-2 font-medium">Автор</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.documents.map((doc) => (
                        <tr key={doc.id} className="border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/20 transition-colors cursor-pointer">
                          <td className="px-3 py-2.5 text-[#2D2D2D]">{doc.type}</td>
                          <td className="px-3 py-2.5 text-[#2D6A4F] font-medium">{doc.number}</td>
                          <td className="px-3 py-2.5 text-[#666666]">{doc.date}</td>
                          <td className="px-3 py-2.5 text-right text-[#2D2D2D] font-medium">{doc.amount > 0 ? doc.amount.toLocaleString("ru-RU") : "—"}</td>
                          <td className="px-3 py-2.5">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] ${
                              doc.status === "Оплачен" || doc.status === "Подписан" || doc.status === "Отгружен"
                                ? "bg-[#D4EDDA] text-[#2D6A4F]"
                                : doc.status === "Действует"
                                ? "bg-[#FEF3C7] text-[#2D2D2D]"
                                : "bg-[#FDF2B5] text-[#2D2D2D]"
                            }`}>{doc.status}</span>
                          </td>
                          <td className="px-3 py-2.5 text-[#666666]">{doc.author}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "Файлы" && (
              <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-[#2D2D2D]">Файлы ({data.files.length})</span>
                  <button className="flex items-center gap-1 text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">
                    <i className="ri-upload-line text-[10px]"></i>
                    Загрузить файл
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="bg-[#FDF2B5] text-[#666666]">
                        <th className="text-left px-3 py-2 font-medium">Имя файла</th>
                        <th className="text-left px-3 py-2 font-medium">Тип</th>
                        <th className="text-right px-3 py-2 font-medium">Размер</th>
                        <th className="text-left px-3 py-2 font-medium">Дата</th>
                        <th className="text-left px-3 py-2 font-medium">Автор</th>
                        <th className="text-center px-3 py-2 font-medium w-[60px]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.files.map((file, i) => (
                        <tr key={i} className="border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/20 transition-colors cursor-pointer">
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <i className={`${file.type === "PDF" ? "ri-file-pdf-2-line text-[#DC2626]" : file.type === "Excel" ? "ri-file-excel-2-line text-[#2D6A4F]" : "ri-folder-zip-line text-[#D5CA8A]"} text-[14px]`}></i>
                              <span className="text-[#2D6A4F] hover:underline font-medium">{file.name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-[#666666]">{file.type}</td>
                          <td className="px-3 py-2.5 text-right text-[#666666]">{file.size}</td>
                          <td className="px-3 py-2.5 text-[#666666]">{file.date}</td>
                          <td className="px-3 py-2.5 text-[#666666]">{file.author}</td>
                          <td className="px-3 py-2.5 text-center">
                            <button className="w-5 h-5 flex items-center justify-center rounded hover:bg-[#FDF2B5] cursor-pointer text-[#666666]">
                              <i className="ri-download-line text-[11px]"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "Задачи" && (
              <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-[#2D2D2D]">Задачи ({data.tasks.length})</span>
                  <button className="flex items-center gap-1 text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">
                    <i className="ri-add-line text-[10px]"></i>
                    Создать задачу
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="bg-[#FDF2B5] text-[#666666]">
                        <th className="text-left px-3 py-2 font-medium">ID</th>
                        <th className="text-left px-3 py-2 font-medium">Задача</th>
                        <th className="text-left px-3 py-2 font-medium">Срок</th>
                        <th className="text-center px-3 py-2 font-medium">Приоритет</th>
                        <th className="text-left px-3 py-2 font-medium">Статус</th>
                        <th className="text-left px-3 py-2 font-medium">Исполнитель</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.tasks.map((task) => (
                        <tr key={task.id} className="border-b border-[#D5CA8A]/30 hover:bg-[#FDF2B5]/20 transition-colors cursor-pointer">
                          <td className="px-3 py-2.5 text-[#2D6A4F] font-medium">{task.id}</td>
                          <td className="px-3 py-2.5 text-[#2D2D2D] font-medium">{task.title}</td>
                          <td className="px-3 py-2.5 text-[#666666]">{task.dueDate}</td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] ${
                              task.priority === "Высокий" ? "bg-[#FFEDD5] text-[#DC2626]" : "bg-[#FDF2B5] text-[#2D2D2D]"
                            }`}>{task.priority}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] ${
                              task.status === "Выполнена" ? "bg-[#D4EDDA] text-[#2D6A4F]" : task.status === "В работе" ? "bg-[#FEF3C7] text-[#2D2D2D]" : "bg-[#FDF2B5] text-[#666666]"
                            }`}>{task.status}</span>
                          </td>
                          <td className="px-3 py-2.5 text-[#666666]">{task.assignee}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "Примечания" && (
              <div className="bg-white rounded-lg border border-[#D5CA8A] overflow-hidden">
                <div className="px-3 py-2 bg-[#FEF3C7] border-b border-[#D5CA8A] flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-[#2D2D2D]">Примечания ({data.notes.length})</span>
                  <button className="flex items-center gap-1 text-[10px] text-[#2D6A4F] hover:underline cursor-pointer">
                    <i className="ri-add-line text-[10px]"></i>
                    Добавить примечание
                  </button>
                </div>
                <div className="p-3 space-y-3">
                  {data.notes.map((note) => (
                    <div key={note.id} className="bg-[#FDF2B5]/30 rounded p-3 border border-[#D5CA8A]/30">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-[#666666]">{note.id}</span>
                          <span className="text-[10px] text-[#666666]">{note.author}</span>
                        </div>
                        <span className="text-[9px] text-[#666666]">{note.date}</span>
                      </div>
                      <p className="text-[10px] text-[#2D2D2D] leading-relaxed">{note.text}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <button className="text-[9px] text-[#666666] hover:text-[#2D2D2D] cursor-pointer">
                          <i className="ri-edit-line text-[10px] mr-0.5"></i>Редактировать
                        </button>
                        <button className="text-[9px] text-[#DC2626] hover:text-[#991B1B] cursor-pointer">
                          <i className="ri-delete-bin-line text-[10px] mr-0.5"></i>Удалить
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Bottom status bar */}
          <div className="h-[24px] flex items-center px-2 mx-4 mb-2 bg-[#FBED9E] border border-[#D5CA8A] text-[10px] text-[#666666] select-none rounded">
            <span>Последнее обновление: 24.06.2026 | Клиент: {data.name} | Статус: {clientStatus}</span>
            <span className="ml-auto">1С:Предприятие 8.3.25.1448</span>
          </div>
        </div>
      </div>
    </div>
  );
}